/**
 * Os itens do RPG, do lado da tela: de onde vêm, para onde vão e como se leem.
 *
 * O acervo entrega o item com os efeitos já traduzidos pelo pipeline (`heal`,
 * `healRate`, `cures`, `opensChests`, `boost` — ver ASSETS.md). Daqui saem
 * três respostas que várias telas precisam dar do mesmo jeito:
 *
 *   - o `ItemDef` que o motor usa em campo;
 *   - o DESTINO do item — batalha, ficha ou nenhum —, a mesma regra de
 *     `Oz\Content\ItemUse` no servidor, com testes espelhados;
 *   - a linha que descreve um espaço da bolsa, arma ou item.
 */

import {
  CONDITIONS,
  isConditionId,
  isConsumable,
  itemUseOf,
  type ConditionId,
  type Content,
  type ItemDef,
  type ItemInstance,
  type ItemUse,
  type StatKey,
  type WeaponDef,
} from '@oz/rules';

/** Um item como a API e o acervo o entregam. */
export interface AcervoItem {
  readonly id: number;
  readonly name: string;
  readonly description?: string | undefined;
  readonly icon?: number | undefined;
  readonly iconSheet?: string | undefined;
  readonly uses?: number | undefined;
  readonly heal?: number | undefined;
  readonly healRate?: number | undefined;
  readonly cures?: readonly string[] | undefined;
  readonly opensChests?: boolean | undefined;
  /** Aumento permanente de atributo, usado na ficha e não em campo. */
  readonly boost?: { readonly stat: string; readonly amount: number } | undefined;
  readonly pack?: string | null | undefined;
}

/** Os atributos que um reforço pode aumentar — os oito do motor. */
const BOOSTABLE: readonly StatKey[] = ['hp', 'str', 'mag', 'skl', 'spd', 'lck', 'def', 'res'];

export const STAT_LABELS: Readonly<Record<StatKey, string>> = {
  hp: 'PV', str: 'Força', mag: 'Magia', skl: 'Destreza',
  spd: 'Velocidade', lck: 'Sorte', def: 'Defesa', res: 'Resistência',
};

function curesOf(raw: AcervoItem): ConditionId[] {
  return (raw.cures ?? []).filter(isConditionId);
}

/** O item no formato do motor. */
export function toItemDef(raw: AcervoItem): ItemDef {
  const cures = curesOf(raw);
  return {
    id: raw.id,
    name: raw.name,
    uses: Math.max(1, Math.floor(raw.uses ?? 1)),
    ...(raw.heal && raw.heal > 0 ? { heal: Math.floor(raw.heal) } : {}),
    ...(raw.healRate && raw.healRate > 0 ? { healRate: Math.min(1, raw.healRate) } : {}),
    ...(cures.length > 0 ? { cures } : {}),
    ...(raw.opensChests ? { opensChests: true } : {}),
  };
}

/** O reforço válido do item, se houver. */
export function boostOf(raw: AcervoItem): { stat: StatKey; amount: number } | null {
  const boost = raw.boost;
  if (!boost || !(BOOSTABLE as readonly string[]).includes(boost.stat)) return null;
  const amount = Math.floor(boost.amount);
  return amount > 0 ? { stat: boost.stat as StatKey, amount } : null;
}

export type ItemDestiny = 'batalha' | 'ficha' | 'nenhum';

/**
 * Para onde o item vai.
 *
 * **batalha** — cura PV, cura condição ou abre baú: vai para a bolsa de um
 * personagem. **ficha** — aumenta um atributo para sempre: usa-se na aba
 * Comunidade, e o servidor aplica. **nenhum** — o efeito do RPG não tem
 * equivalente no tabuleiro (PM, reviver, exploração); fica na conta.
 */
export function itemDestiny(raw: AcervoItem): ItemDestiny {
  const def = toItemDef(raw);
  if ((def.heal ?? 0) > 0 || (def.healRate ?? 0) > 0 || (def.cures?.length ?? 0) > 0 || def.opensChests) {
    return 'batalha';
  }
  return boostOf(raw) ? 'ficha' : 'nenhum';
}

export function conditionLabel(id: ConditionId): string {
  return CONDITIONS[id].label;
}

/** "Envenenado e Paralisado", "todas as condições". */
export function conditionList(ids: readonly ConditionId[]): string {
  if (ids.length >= Object.keys(CONDITIONS).length) return 'todas as condições';
  const labels = ids.map(conditionLabel);
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} e ${labels[labels.length - 1]}`;
}

/** O que o uso faz, numa linha: "Recupera 23 PV · cura Envenenado". */
export function describeUse(use: ItemUse): string {
  const parts: string[] = [];
  if (use.healRate >= 1) parts.push('Recupera todo o PV');
  else if (use.healRate > 0) parts.push(`Recupera ${Math.round(use.healRate * 100)}% do PV`);
  if (use.heal > 0) parts.push(use.healRate > 0 ? `+${use.heal} PV` : `Recupera ${use.heal} PV`);
  if (use.cures.length > 0) parts.push(`cura ${conditionList(use.cures)}`);
  return parts.join(' · ');
}

/** O que a tela precisa para desenhar um espaço da bolsa. */
export interface SlotView {
  readonly kind: 'arma' | 'item';
  readonly name: string;
  readonly icon?: number | undefined;
  readonly iconSheet?: string | undefined;
  /** Tipo de arma, para o ícone de reserva. */
  readonly type?: string | undefined;
  /** Usa-se (bebe, aplica) em vez de empunhar. */
  readonly usable: boolean;
  /** Pode ir para a mão. */
  readonly equippable: boolean;
  /** A linha de detalhe. */
  readonly detail: string;
}

type IconFields = { icon?: number; iconSheet?: string };

export function slotView(
  content: Content,
  catalog: { readonly weapons: readonly WeaponDef[]; readonly items?: readonly AcervoItem[] | undefined },
  slot: ItemInstance,
): SlotView {
  const use = itemUseOf(content, slot);
  if (slot.weaponId !== undefined) {
    const weapon = catalog.weapons.find((w) => w.id === slot.weaponId);
    const art = weapon as (WeaponDef & IconFields) | undefined;
    const detail = !weapon
      ? ''
      : use
        ? `${describeUse(use)} · encerra o turno`
        : weapon.opensChests
          ? 'Abre um baú trancado'
          : `${weapon.heals ? 'Cura' : 'Pod'} ${weapon.might} · Alc ${weapon.range[0]}–${weapon.range[1]}`;
    return {
      kind: 'arma',
      name: weapon?.name ?? `Arma ${slot.weaponId}`,
      icon: art?.icon,
      iconSheet: art?.iconSheet,
      type: weapon?.type,
      usable: isConsumable(weapon),
      equippable: Boolean(weapon) && !weapon?.opensChests,
      detail,
    };
  }

  const raw = catalog.items?.find((i) => i.id === slot.itemId);
  const def = content.items.get(slot.itemId);
  return {
    kind: 'item',
    name: raw?.name ?? def?.name ?? `Item ${slot.itemId}`,
    icon: raw?.icon,
    iconSheet: raw?.iconSheet,
    usable: use !== null,
    equippable: false,
    detail: use
      ? `${describeUse(use)} · em si ou num aliado ao lado`
      : def?.opensChests
        ? 'Abre um baú trancado'
        : 'Sem efeito em campo',
  };
}
